import React, { useState } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  LinearProgress,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useVendorAuth } from '../../contexts/VendorAuthContext';
import { useTokenValidation } from '../../hooks/useTokenValidation';

// S3 asset URL
const logoImg = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png';

const OnboardingStep1: React.FC = () => {
  usePageTitle('Setup Password');
  
  const navigate = useNavigate();
  const { vendor, setPassword, isLoading } = useVendorAuth();
  const { isAuthenticated, validationError, hasToken } = useTokenValidation();
  
  const [password, setPasswordState] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isPasswordSet, setIsPasswordSet] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validatePasswords = () => {
    if (password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validatePasswords()) {
      return;
    }

    const success = await setPassword(password);
    
    if (success) {
      setIsPasswordSet(true);
      // Navigate to next step after a brief delay
      setTimeout(() => {
        navigate('/onboarding/step2');
      }, 1500);
    }
  };

  const progress = 20; // 1 of 5 steps = 20%

  // Show loading state while validating token
  if (isLoading && !isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 0',
          width: '100vw'
        }}
      >
        <CircularProgress size={60} sx={{ color: '#67314f', marginBottom: '20px' }} />
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            color: '#181818',
            textAlign: 'center'
          }}
        >
          Validating your access...
        </Typography>
      </Box>
    );
  }

  // Show error state if token validation failed
  if (validationError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 20px',
          width: '100vw'
        }}
      >
        <Box sx={{ maxWidth: '540px', width: '100%' }}>
          <Alert severity="error" sx={{ marginBottom: '20px' }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                marginBottom: '8px'
              }}
            >
              Access Denied
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '14px'
              }}
            >
              {validationError}
            </Typography>
          </Alert>
          <Button
            variant="outlined"
            onClick={() => window.location.href = 'mailto:support@eventi.com'}
            sx={{
              borderColor: '#67314f',
              color: '#67314f',
              fontFamily: '"Inter", sans-serif',
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Contact Support
          </Button>
        </Box>
      </Box>
    );
  }

  // If vendor is authenticated and doesn't need password setup, redirect to next step
  // Only redirect if we're sure the vendor has completed password setup
  if (isAuthenticated && vendor && !vendor.needsPasswordSetup && vendor.onboardingStep > 0) {
    navigate('/onboarding/step2', { replace: true });
    return null;
  }

  // Show form if:
  // 1. Vendor is authenticated and needs password setup, OR
  // 2. Token validation is in progress, OR  
  // 3. There's a token in URL (will be validated by useTokenValidation)
  if (!isAuthenticated && !hasToken && !isLoading) {
    // No token and not authenticated - redirect to login
    navigate('/login', { replace: true });
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 0',
        width: '100vw'
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          width: '120px',
          height: '94px',
          marginBottom: '40px'
        }}
      >
        <Box
          component="img"
          src={logoImg}
          alt="Eventi Logo"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Main Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          width: '100%',
          maxWidth: '540px',
          padding: '0 20px'
        }}
      >
        {/* Title Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: '44px',
              lineHeight: 1.15,
              letterSpacing: '-0.44px',
              color: '#181818',
              marginBottom: '16px',
              textAlign: 'left'
            }}
          >
            Set your password
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: 1.4,
              color: '#181818',
              marginBottom: '16px',
              textAlign: 'left'
            }}
          >
            Create a secure password for your account. You'll use this password to access your vendor dashboard.
          </Typography>
          
          {/* Show vendor information if authenticated */}
          {isAuthenticated && vendor && (
            <Box
              sx={{
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '16px'
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#181818',
                  marginBottom: '4px'
                }}
              >
                Welcome, {vendor.contactName}!
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  color: '#93989c'
                }}
              >
                {vendor.email}
              </Typography>
            </Box>
          )}
          
          {/* Progress Indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                color: '#181818'
              }}
            >
              1 of 5
            </Typography>
          </Box>
          
          {/* Progress Bar */}
          <Box sx={{ width: '100%', margin: '0 auto' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: '4px',
                borderRadius: '2px',
                backgroundColor: '#f0f0f0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#67314f',
                  borderRadius: '2px'
                }
              }}
            />
          </Box>
        </Box>

        {/* Success Message or Form Section */}
        {isPasswordSet ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              textAlign: 'center'
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 600,
                fontSize: '20px',
                color: '#67314f',
                marginBottom: '8px'
              }}
            >
              Password Set Successfully!
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '16px',
                color: '#181818'
              }}
            >
              Redirecting to the next step...
            </Typography>
          </Box>
        ) : (
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
          {/* Section Title */}
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: 1.2,
              color: '#181818',
              marginBottom: '8px'
            }}
          >
            Account credentials
          </Typography>

          {/* Email Field (Disabled) */}
          <TextField
            fullWidth
            placeholder="Email"
            value={vendor?.email || ''}
            disabled
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#e8e8e8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e8e8e8'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '16px',
                color: '#93989c',
                '&.Mui-disabled': {
                  WebkitTextFillColor: '#93989c'
                }
              }
            }}
          />

          {/* Password Field */}
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPasswordState(e.target.value);
              setPasswordError('');
            }}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    sx={{ color: '#93989c' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                paddingRight: '8px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '16px',
                color: password ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Confirm Password Field */}
          <TextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setPasswordError('');
            }}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleToggleConfirmPasswordVisibility}
                    edge="end"
                    sx={{ color: '#93989c' }}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                paddingRight: '8px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"Inter", sans-serif',
                fontSize: '16px',
                color: confirmPassword ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Password Error Message */}
          {passwordError && (
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontSize: '14px',
                color: '#d32f2f',
                marginTop: '-8px'
              }}
            >
              {passwordError}
            </Typography>
          )}

          {/* Password Requirements */}
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontSize: '14px',
              color: '#93989c',
              marginTop: '-8px'
            }}
          >
            Password must be at least 8 characters long
          </Typography>

          {/* Continue Button */}
          <Button
            type="submit"
            fullWidth
            disabled={!password || !confirmPassword || isLoading}
            sx={{
              height: '56px',
              backgroundColor: '#67314f',
              borderRadius: '16px',
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: 'white',
              marginTop: '16px',
              '&:hover': {
                backgroundColor: '#5a2a42'
              },
              '&:disabled': {
                backgroundColor: '#93989c',
                color: 'white'
              }
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: 'white' }} />
            ) : (
              'Continue'
            )}
          </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OnboardingStep1;

